'use client'

import type { ConnectivityKind } from '@/lib/queryNetwork'

export interface ConnectivityResourceState {
  id: string
  label: string
  /** Primary query for this resource failed while optional cached rows remain. */
  hasCachedData: boolean
  isFetching: boolean
  errorKind: ConnectivityKind | null
  errorTitle: string | null
  errorMessage: string | null
  lastMutationErrorTitle: string | null
  lastMutationErrorMessage: string | null
}

export interface ConnectivitySnapshot {
  isOnline: boolean
  globalSlow: boolean
  trackedFetchInFlight: number
  resources: Record<string, ConnectivityResourceState>
}

const EMPTY_RESOURCE: Omit<ConnectivityResourceState, 'id' | 'label'> = {
  hasCachedData: false,
  isFetching: false,
  errorKind: null,
  errorTitle: null,
  errorMessage: null,
  lastMutationErrorTitle: null,
  lastMutationErrorMessage: null,
}

function createEmptySnapshot(): ConnectivitySnapshot {
  return {
    isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
    globalSlow: false,
    trackedFetchInFlight: 0,
    resources: {},
  }
}

let snapshot: ConnectivitySnapshot = createEmptySnapshot()
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) {
    listener()
  }
}

function setSnapshot(next: ConnectivitySnapshot) {
  snapshot = next
  emit()
}

export function subscribeConnectivity(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getConnectivitySnapshot(): ConnectivitySnapshot {
  return snapshot
}

export function setConnectivityOnline(isOnline: boolean) {
  if (snapshot.isOnline === isOnline) return
  setSnapshot({ ...snapshot, isOnline })
}

export function setConnectivityGlobalSlow(globalSlow: boolean) {
  if (snapshot.globalSlow === globalSlow) return
  setSnapshot({ ...snapshot, globalSlow })
}

export function adjustTrackedFetchInFlight(delta: number) {
  const trackedFetchInFlight = Math.max(0, snapshot.trackedFetchInFlight + delta)
  if (trackedFetchInFlight === snapshot.trackedFetchInFlight) return
  setSnapshot({ ...snapshot, trackedFetchInFlight })
}

function ensureResource(id: string, label: string): ConnectivityResourceState {
  const existing = snapshot.resources[id]
  if (existing) return existing
  return { id, label, ...EMPTY_RESOURCE }
}

export function patchConnectivityResource(
  id: string,
  label: string,
  patch: Partial<Omit<ConnectivityResourceState, 'id' | 'label'>>
) {
  const current = ensureResource(id, label)
  const next: ConnectivityResourceState = { ...current, ...patch, id, label }

  const unchanged =
    current.hasCachedData === next.hasCachedData &&
    current.isFetching === next.isFetching &&
    current.errorKind === next.errorKind &&
    current.errorTitle === next.errorTitle &&
    current.errorMessage === next.errorMessage &&
    current.lastMutationErrorTitle === next.lastMutationErrorTitle &&
    current.lastMutationErrorMessage === next.lastMutationErrorMessage

  if (unchanged) return

  setSnapshot({
    ...snapshot,
    resources: { ...snapshot.resources, [id]: next },
  })
}

export function clearConnectivityResourceMutationError(id: string) {
  const current = snapshot.resources[id]
  if (!current?.lastMutationErrorMessage && !current?.lastMutationErrorTitle) return
  patchConnectivityResource(id, current.label, {
    lastMutationErrorTitle: null,
    lastMutationErrorMessage: null,
  })
}

/** Test-only reset */
export function resetConnectivityStoreForTests() {
  snapshot = createEmptySnapshot()
  emit()
}
